import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Ship, Truck, Store, PackageCheck, ShieldCheck, Languages, Wallet } from "lucide-react";

const HERO = "https://static.prod-images.emergentagent.com/jobs/510c482e-1442-4fed-aae2-6a40aac45335/images/1f994e1bc74341a6580bde4b33a8f11296152aa5a197a545329056e88fcf843e.jpeg";

const steps = [
  { icon: Search, title: "Cari Barang", desc: "Ketik barang pakai Bahasa Indonesia, kami carikan dari ribuan supplier China." },
  { icon: Wallet, title: "Bayar Rupiah", desc: "Harga otomatis tampil dalam Rupiah, tanpa ribet hitung kurs sendiri." },
  { icon: Ship, title: "Kami Kirim ke Indo", desc: "Barang dibeli & dikirim dari China sampai gudang kami di Indonesia." },
  { icon: Store, title: "Ambil di Kedai", desc: "Ambil pesanan di kedai daerah terdekat, dibantu agen lokal." },
];

export default function Home() {
  const [q, setQ] = useState("");
  const nav = useNavigate();

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-emerald-50 to-stone-50 grain">
        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs font-semibold uppercase tracking-widest text-emerald-700 bg-emerald-100 px-3 py-1 rounded-full">Belanja Grosir Langsung dari China</span>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-stone-900 leading-[1.05]">
              Belanja Grosir <span className="text-emerald-600">China</span> Semudah Belanja di <span className="text-secondary">Warung</span>
            </h1>
            <p className="mt-5 text-base text-stone-600 leading-relaxed max-w-lg">
              Cari barang grosir pakai Bahasa Indonesia, harga langsung Rupiah, kami bantu kirim
              sampai gudang Indonesia — lalu ambil di kedai terdekat dengan pendampingan agen lokal.
            </p>
            <form onSubmit={(e) => { e.preventDefault(); nav(`/katalog?q=${encodeURIComponent(q)}`); }}
              className="mt-7 flex items-center bg-white rounded-full shadow-sm border border-stone-200 p-1.5 max-w-lg">
              <Search size={20} className="text-stone-400 ml-3" />
              <input data-testid="hero-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Contoh: sepatu, wajan, mainan anak..."
                className="flex-1 bg-transparent outline-none px-3 text-sm" />
              <button data-testid="hero-search-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm px-6 py-2.5 rounded-full transition">Cari Barang</button>
            </form>
            <div className="mt-4 flex flex-wrap gap-2 text-xs text-stone-500">
              {["sepatu", "kaos polos", "wajan anti lengket", "mainan edukasi"].map((t) => (
                <button key={t} onClick={() => nav(`/katalog?q=${encodeURIComponent(t)}`)} className="px-3 py-1 rounded-full bg-white border border-stone-200 hover:border-emerald-400 transition">{t}</button>
              ))}
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="relative">
            <img src={HERO} alt="Alur jastip China ke Indonesia" className="rounded-3xl shadow-2xl w-full border border-stone-200" />
            <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-lg px-4 py-3 border border-stone-100 hidden sm:block">
              <p className="text-xs text-stone-500">Terhubung dengan</p>
              <p className="font-display font-bold text-emerald-700 text-sm">1.000+ Supplier China</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <span className="text-xs font-semibold uppercase tracking-widest text-emerald-700">Cara Kerja</span>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-stone-900 mt-2">4 Langkah Mudah, Tanpa Ribet</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="bg-white rounded-2xl border border-stone-200 p-6 hover:shadow-md transition">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-700 mb-4"><s.icon size={24} /></div>
              <div className="text-xs font-mono text-stone-400 mb-1">0{i + 1}</div>
              <h3 className="font-display font-semibold text-lg text-stone-900">{s.title}</h3>
              <p className="text-sm text-stone-500 mt-1.5 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SHIPPING */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <span className="text-xs font-semibold uppercase tracking-widest text-emerald-700">Pengiriman</span>
          <h2 className="font-display text-3xl font-bold text-stone-900 mt-1">Pilihan Ongkos Kirim</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl border border-stone-200 p-7">
            <div className="flex items-center gap-2 text-sky-600 mb-3"><Ship size={24} /><h3 className="font-display font-semibold text-lg text-stone-900">Via Laut</h3></div>
            <p className="text-3xl font-display font-extrabold text-emerald-700">Rp25rb<span className="text-xl text-stone-400"> – 50rb</span><span className="text-sm font-normal text-stone-500"> / kg</span></p>
            <p className="text-sm text-stone-500 mt-2">Estimasi <b>40–100 hari</b>. Minimal <b>100 kg</b>. Sudah termasuk ongkir + pajak masuk Indonesia. Barang ringan dihitung per CBM.</p>
          </div>
          <div className="bg-white rounded-2xl border border-stone-200 p-7">
            <div className="flex items-center gap-2 text-emerald-600 mb-3"><PackageCheck size={24} /><h3 className="font-display font-semibold text-lg text-stone-900">Via Pesawat</h3></div>
            <p className="text-3xl font-display font-extrabold text-emerald-700">Rp325rb<span className="text-xl text-stone-400"> – 485rb</span></p>
            <p className="text-sm text-stone-500 mt-2">Estimasi <b>12–20 hari</b> sampai kedai. Minimal <b>1 kg</b> — cocok beli sample. Sudah termasuk ongkir dari China, Jakarta & pajak masuk.</p>
          </div>
          <div className="bg-emerald-900 text-white rounded-2xl p-7">
            <div className="flex items-center gap-2 text-emerald-300 mb-3"><Truck size={24} /><h3 className="font-display font-semibold text-lg">Di atas 2 Ton</h3></div>
            <p className="text-2xl font-display font-extrabold">Nego + Diskon</p>
            <p className="text-sm text-emerald-100/80 mt-2">Barang berat &amp; besar (&gt;2 ton) bisa didiskusikan dengan admin &amp; dapat harga diskon khusus.</p>
          </div>
        </div>
        <p className="text-center text-sm text-stone-500 max-w-3xl mx-auto mt-6">Semua tarif ongkir di atas <b>sudah termasuk</b> ongkos kirim dari China ke Jakarta, dari Jakarta ke kedai terdekatmu, <b>dan pajak masuk Indonesia</b>. Jadi kamu tak perlu memikirkan pajak atau biaya tambahan lagi — semuanya sudah kami urus.</p>
      </section>

      {/* TRUST + KEDAI */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Languages, title: "Cari Bahasa Indonesia", desc: "Ketik Bahasa Indonesia, kami carikan langsung ke supplier di China." },
            { icon: ShieldCheck, title: "Garansi Sampai", desc: "Kami tanggung jawab barang sampai gudang & ke kedai tujuan." },
            { icon: PackageCheck, title: "Pendampingan Kedai", desc: "Agen kedai daerah bantu warga awam pesan & ambil barang." },
          ].map((t) => (
            <div key={t.title} className="bg-emerald-900 text-white rounded-2xl p-7">
              <t.icon size={26} className="text-emerald-300 mb-3" />
              <h3 className="font-display font-semibold text-lg">{t.title}</h3>
              <p className="text-sm text-emerald-100/80 mt-1.5 leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
